<template>
  <div>
    <v-stepper-step :complete="currentStep > 1" step="1">
      <h3>Step 1</h3>      
      <div v-show="currentStep == 1" class="text-caption">
        <p>
          <span class="red--text"><strong>* </strong></span>= Mandatory field
        </p>
      </div>
    </v-stepper-step>
    <v-stepper-content step="1">
      <v-form v-model="noticeFormValid">
        <fieldset class="sr-only-fieldset">
          <legend class="sr-only">Notification information</legend>
          <v-container fluid>
              <div class="input-wrapper">
                  <input id="stuff" hidden />
                  <label for="stuff" class="fa fa-exclamation input-icon" aria-hidden="true"></label>
              </div>

              <v-col cols="12" md="4">
                  <v-select :items="noticeTypes" label="Colour of Notification" item-text="name" item-value="id"
                            placeholder="Select notification type" v-model="notice.type" required aria-required="true" :rules="noticeRules" @change="resetAfterNoticeTypeChanged(notice.type)">
                      <template #label>
                          <strong class="black--text">
                              <span class="red--text"><strong>* </strong></span>Colour of Notification:
                              <span class="sr-only"> (required)</span>
                          </strong>
                      </template>
                  </v-select>
              </v-col>

              <v-col cols="12" md="4" v-if="notice.type">
                  <small>
                      <b>
                          <span class="sr-only">You can only request a re-delivery for items where the date of the first notification is not more than {{maxWorkingDaysBeforeRTS}} working days from the notice date. After the {{maxWorkingDaysBeforeRTS}}th working day, we can keep your item for up to 12 weeks.</span>
                          <span aria-hidden="true">You can only request a re-delivery for items where the date of the 1<sup>st</sup> notification is not more than {{maxWorkingDaysBeforeRTS}} working days from the notice date. After the {{maxWorkingDaysBeforeRTS}}<sup>th</sup> working day, we can keep your item for up to 12 weeks.</span>
                      </b>
                  </small>
              </v-col>

              <v-col cols="12" sm="6" md="4" v-if="notice.type">
                  <v-menu v-model="dpMenu" :close-on-content-click="false" :nudge-left="20" transition="scale-transition"
                          offset-y min-width="auto" required>
                      <template v-slot:activator="{ on, attrs }">
                          <v-text-field v-model="date" readonly v-bind="attrs" v-on="on" ref="dateField"
                                        @keydown.enter.native="dpOpenMenu"
                                        @keydown.space.native="dpOpenMenu"
                                        placeholder="Select date" :rules="noticeDateRules"
                                        aria-haspopup="dialog">
                              <template #label>
                                  <strong class="black--text">
                                      <span class="red--text"><strong>* </strong></span>Date of Notification:
                                      <span class="sr-only"> (required)</span>
                                  </strong>
                              </template>
                              <template v-slot:append>
                                  <v-btn icon small
                                         aria-label="Choose date"
                                         aria-haspopup="dialog"
                                         :aria-expanded="dpMenu ? 'true' : 'false'"
                                         @click="dpOpenMenu"
                                         @keydown.enter.native="dpOpenMenu"
                                         @keydown.space.native="dpOpenMenu">
                                      <v-icon>mdi-calendar</v-icon>
                                  </v-btn>
                              </template>
                          </v-text-field>
                      </template>
                      <v-date-picker v-model="date" ref="datePicker" :picker-date.sync="dpPickerDate" :active-picker.sync="dpActivePicker" @input="dpOnSelect" @keydown.native="onDatePickerKeydown" :allowed-dates="disableOutOfRangeDates" required>
                      </v-date-picker>
                  </v-menu>
              </v-col>

              <v-col cols="12" md="4" v-if="notice.type && notice.type != 4">
                  <fieldset class="sr-only-fieldset">
                      <legend class="sr-only">What type of item are you requesting redelivery for?</legend>
                      <v-radio-group v-model="notice.parcelType" :rules="[v => !!v || 'Type is required']">
                          <v-col cols="12">
                              <v-radio label="Untracked" :value="parcelTypes.Untracked" style="display: inline-block;"></v-radio>
                              <v-btn icon x-small aria-label="View untracked notice sample" @click="untrackedDialog = true" style="margin-bottom: 6px;margin-left: 10px;"><v-icon>mdi-information-outline</v-icon></v-btn>
                          </v-col>
                          <v-col cols="12">
                              <v-radio label="Tracked" :value="parcelTypes.Tracked" style="display: inline-block;"></v-radio>
                              <v-btn icon x-small aria-label="View tracked notice sample" @click="trackedDialog = true" style="margin-bottom: 6px;margin-left: 10px;"><v-icon>mdi-information-outline</v-icon></v-btn>
                          </v-col>
                          <template #label>
                              <strong class="black--text" style="font-size:14px">
                                  <span class="red--text"><strong>* </strong></span>Type:
                                  <span class="sr-only"> (required)</span>
                              </strong>
                          </template>
                      </v-radio-group>
                  </fieldset>
              </v-col>

              <template>
                  <div class="text-center">
                      <v-dialog v-model="untrackedDialog"
                                width="1000">

                          <v-card style="padding-bottom: 30px;">
                              <v-card-title class="text-h6 grey lighten-2">
                                  Untracked Notice Sample

                                  <v-spacer></v-spacer>

                                  <v-btn icon @click="untrackedDialog = false" aria-label="Close">
                                      <v-icon>mdi-close</v-icon>
                                  </v-btn>
                              </v-card-title>

                              <v-img max-height="600"
                                     src="~@/assets/images/untrackedNoticeSample.png"
                                     alt="Sample image of an untracked postal notification slip"></v-img>
                          </v-card>
                      </v-dialog>
                  </div>
              </template>

              <template>
                  <div class="text-center">
                      <v-dialog v-model="trackedDialog"
                                width="1000">

                          <v-card style="padding-bottom: 30px;">
                              <v-card-title class="text-h6 grey lighten-2">
                                  Tracked Notice Sample

                                  <v-spacer></v-spacer>

                                  <v-btn icon @click="trackedDialog = false" aria-label="Close">
                                      <v-icon>mdi-close</v-icon>
                                  </v-btn>
                              </v-card-title>

                              <v-img max-height="600"
                                     src="~@/assets/images/trackedNoticeSample.png"
                                     alt="Sample image of a tracked postal notification slip"></v-img>
                          </v-card>
                      </v-dialog>
                  </div>
              </template>

              <v-col cols="12" md="4" v-if="notice.type">
                  <v-file-input v-model="notice.file" style="flex-direction: row-reverse;" :rules=notificationFileRulesTest(notice.existingFileName)
                                accept="image/*,.pdf,.doc" placeholder="Upload copy of your notification" required aria-required="true"
                                aria-describedby="file-size-hint file-multi-hint file-deletion-hint">
                      <template #label>
                          <strong class="black--text">
                              <span class="red--text"><strong>* </strong></span>Copy of Notification File:
                              <span class="sr-only"> (required)</span>
                          </strong>
                      </template>
                  </v-file-input>
                  <div v-if="dataPreloadedFromDb && notice.existingFileName">Current file: {{notice.existingFileName}}</div>
              </v-col>

              <v-col cols="12" md="4" v-if="notice.type">
                  <small>File size allowed up to 12 MB.</small>
              </v-col>

              <v-col cols="12" md="4" v-if="notice.type">
                  <small>In case you have more than one notification to upload (related to up to 3 different items), we recommend taking one picture of them side by side to upload them in one file.</small>
              </v-col>

              <v-col cols="12" md="4" v-if="notice.type">
                  <small>Notification file uploads will be deleted from our system after 3 months.</small>
              </v-col>

              <v-col cols="12" md="4" v-if="notice.type && (notice.type == 4 || notice.parcelType === parcelTypes.Tracked)">
                  <v-text-field v-model="notice.trackingNumber1"
                                :rules="notice.type && ((notice.type == 4 || notice.parcelType === parcelTypes.Tracked) ? requiredTrackingNumberRules : [])"
                                label="Tracking Number" placeholder="Enter Tracking Number"
                                :required="(notice.type == 4 || notice.parcelType === parcelTypes.Tracked)"
                                :aria-required="(notice.type == 4 || notice.parcelType === parcelTypes.Tracked) ? 'true' : null"
                                :error-messages=errorMessages(this.notice.trackingNumber1)>
                      <template #label>
                          <strong class="black--text">
                              <span class="red--text">
                                  <strong v-if="(notice.type && notice.type == 4) || notice.parcelType === parcelTypes.Tracked">* </strong>
                              </span>
                              Tracking Number:
                              <span v-if="(notice.type && notice.type == 4) || notice.parcelType === parcelTypes.Tracked" class="sr-only"> (required)</span>
                          </strong>
                      </template>
                  </v-text-field>
              </v-col>

              <v-col cols="12" md="4" v-if="notice.type && (notice.type == 4 || notice.parcelType === parcelTypes.Tracked)">
                  <v-text-field v-model="notice.trackingNumber2"
                                :rules="[!notice.trackingNumber2 || /^[a-zA-Z\d]{1,30}$/.test(notice.trackingNumber2) && (notice.trackingNumber2 !== notice.trackingNumber3) && (notice.trackingNumber2 !== notice.trackingNumber1) || 'Invalid Tracking Number.']"
                                label="Tracking Number 2" placeholder="Enter Tracking Number"
                                :error-messages=errorMessages(this.notice.trackingNumber2)>
                      <template #label>
                          <strong class="black--text">
                              <span class="red--text"></span>Tracking Number 2:
                          </strong>
                      </template>
                  </v-text-field>
              </v-col>

              <v-col cols="12" md="4" v-if="notice.type && (notice.type == 4 || notice.parcelType === parcelTypes.Tracked)">
                  <v-text-field v-model="notice.trackingNumber3"
                                :rules="[!notice.trackingNumber3 || /^[a-zA-Z\d]{1,30}$/.test(notice.trackingNumber3) && (notice.trackingNumber2 !== notice.trackingNumber3) && (notice.trackingNumber3 !== notice.trackingNumber1) || 'Invalid Tracking Number.']"
                                label="Tracking Number 3" placeholder="Enter Tracking Number"
                                :error-messages=errorMessages(this.notice.trackingNumber3)>
                      <template #label>
                          <strong class="black--text">
                              <span class="red--text"></span>Tracking Number 3:
                          </strong>
                      </template>
                  </v-text-field>
              </v-col>

          </v-container>
        </fieldset>
      </v-form>
      <v-btn color="primary" @click="goNext()" :disabled="!noticeFormValid" :aria-disabled="!noticeFormValid">
        Next
      </v-btn>
      <div class="sr-only" aria-live="polite" v-if="!noticeFormValid">
        Please complete all required notification fields to proceed.
      </div>
    </v-stepper-content>
  </div>
</template>

<style>

.v-file-input .v-input__prepend-outer {
  margin-right: 0 !important;
}

</style>

<script>
import { createNamespacedHelpers } from "vuex";
import validationRules from "../../helpers/ValidationRules";
import parcelTypes from "../../constants/parcelTypes";
import accessibleDatePicker from "../../mixins/accessibleDatePicker";

const { mapGetters: mapMissedDeliveryGetters } = createNamespacedHelpers(
  "missedDelivery"
);

export default {
  name: "Step01",
  mixins: [accessibleDatePicker],
  data() {
    return {
      ...validationRules,
      noticeFormValid: false,
      dataPreloadedFromDb: false,
      date: null,
      notice: {
        type: "",
        date: null,
        parcelType: "",
        trackingNumber1: "",
        trackingNumber2: "",
        trackingNumber3: "",
        file: null
      },
      dpMenu: false,
      trackedDialog: false,
      untrackedDialog: false,
      parcelTypes: parcelTypes,
      maxWorkingDaysBeforeRTS: 0
    };
  },
  computed: {
    ...mapMissedDeliveryGetters({
        noticeTypes: "noticeTypes", 
        loadedNotice: "Step01/notice"
    }),
    currentStep: {
      set(currentStep) {
        this.$store.dispatch("missedDelivery/setCurrentStep", currentStep);
      },
      get() {
        const stepNumber = this.$store.getters["missedDelivery/currentStep"];
        return stepNumber;
      },
    }
  },
  watch: {
    loadedNotice() {
      if (Object.keys(this.loadedNotice).length !== 0 && !this.dataPreloadedFromDb) {
        this.notice = { ...this.loadedNotice }; //make a "deep" copy
        this.date = new Date(this.notice.date).toISOString().substr(0, 10);
        this.notice.existingFileName = this.loadedNotice.filename;
        this.dataPreloadedFromDb = true;
      }
    }
  },
  methods: {
    dpGetValue() {
      return this.date;
    },
    dpFieldLabel() {
      return "Date of Notification";
    },
    disableOutOfRangeDates(val) {
      var date = new Date(val).setHours(0,0,0,0);
      var noticeType = this.noticeTypes.find(n=>n.id === this.notice.type);
      var upperLimit = new Date(noticeType.upperLimit).setHours(0,0,0,0);
      var lowerLimit = new Date(noticeType.lowerLimit).setHours(0,0,0,0);
      return (date <= upperLimit && date >= lowerLimit) && !noticeType.nonWorkingDaysAndHolidays.some(n=>{
        var notAllowedDay = new Date(n).setHours(0,0,0,0);
        return notAllowedDay == date;});
    },
    goNext() {
      this.notice.date = new Date(this.date);

      if (!this.notice.parcelType || (this.notice.type && this.notice.type == 4)) {
        //Some notice types (e.g. white) do not require the user to select a parcel type (untracked / tracked).
        //Set "Tracked" when parcel type is not set by the user.
        this.notice.parcelType = parcelTypes.Tracked; 
      }

      this.$store.dispatch("missedDelivery/Step01/setNotice", this.notice);

      if (this.notice.parcelType === parcelTypes.Tracked) {
        this.$store.dispatch("missedDelivery/validateTracked", {
          barcodes: [ this.notice.trackingNumber1, this.notice.trackingNumber2, this.notice.trackingNumber3 ]
        });
      }
      else {
        this.$store.dispatch("missedDelivery/validateUntracked", {
          date: this.notice.date
        });
      }
    },
    errorMessages(trackingNumber) {
      const validationErrors = this.$store.getters["missedDelivery/validationErrors"];
      if (validationErrors && trackingNumber in validationErrors) {
        return validationErrors[trackingNumber];
      } else {
        return null;
      }
    },
    resetAfterNoticeTypeChanged() {
      var noticeType = this.noticeTypes.find(n=>n.id === this.notice.type);
      this.maxWorkingDaysBeforeRTS = noticeType.maxWorkingDaysBeforeRTS;
      this.date = null;
    }
  },
  mounted() {
    var barcode = this.$route.query.barcode;
    if (barcode){
      this.notice.trackingNumber1 = barcode;
    }
  }
};
</script>
